import { Bot, Phone, PhoneCall } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { Contact } from '../../../types/leads';

export interface LeadPhoneOption {
  id: string;
  name: string | null;
  phone: string;
}

export interface CallMethodLead {
  id: string;
  fullName?: string | null;
  phone?: string | null;
  contacts?: Contact[];
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

export function getLeadPhoneOptions(lead: CallMethodLead): LeadPhoneOption[] {
  const options: LeadPhoneOption[] = [];
  const seen = new Set<string>();
  if (lead.phone) {
    options.push({ id: `lead-${lead.id}`, name: lead.fullName ?? null, phone: lead.phone });
    seen.add(normalizePhone(lead.phone));
  }
  for (const contact of lead.contacts ?? []) {
    if (!contact.phone) continue;
    const normalized = normalizePhone(contact.phone);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    options.push({ id: contact.id, name: contact.name, phone: contact.phone });
  }
  return options;
}

interface CallMethodSheetProps {
  visible: boolean;
  onClose: () => void;
  lead: CallMethodLead;
  onCallHuman: (phone: string) => void;
  onCallAgent: () => void;
}

function OptionRow({
  icon,
  title,
  hint,
  testID,
  accessibilityLabel,
  onPress,
}: Readonly<{
  icon: React.ReactNode;
  title: string;
  hint?: string;
  testID: string;
  accessibilityLabel: string;
  onPress: () => void;
}>) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [styles.optionRow, pressed && styles.pressed]}
    >
      <HStack spacing="md" align="center">
        <Box style={styles.optionIcon}>{icon}</Box>
        <VStack spacing="xxs" style={{ flex: 1 }}>
          <Text variant="body" color={colors.text.primary} style={{ fontWeight: '600' }}>
            {title}
          </Text>
          {hint ? (
            <Text variant="caption" color={colors.text.secondary} numberOfLines={2}>
              {hint}
            </Text>
          ) : null}
        </VStack>
      </HStack>
    </Pressable>
  );
}

export function CallMethodSheet({
  visible,
  onClose,
  lead,
  onCallHuman,
  onCallAgent,
}: Readonly<CallMethodSheetProps>) {
  const { t } = useTranslation();
  const [choosingContact, setChoosingContact] = useState(false);

  const phoneOptions = useMemo(() => getLeadPhoneOptions(lead), [lead]);

  useEffect(() => {
    if (!visible) setChoosingContact(false);
  }, [visible]);

  const handleHumanPress = () => {
    if (phoneOptions.length > 1) {
      setChoosingContact(true);
      return;
    }
    if (phoneOptions[0]) onCallHuman(phoneOptions[0].phone);
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={
        choosingContact
          ? t('leads.detail.callAction.chooseContact')
          : t('leads.detail.callAction.chooseTitle')
      }
      size="sm"
      position="center"
    >
      <VStack spacing="sm" style={{ padding: tokens.spacing.md }}>
        {choosingContact ? (
          phoneOptions.map((option) => (
            <OptionRow
              key={option.id}
              testID={`call_contact_option_${option.id}`}
              icon={<Phone size={18} color={colors.primary.main} />}
              title={option.name ?? t('leads.detail.callAction.unnamedContact')}
              hint={option.phone}
              accessibilityLabel={`${option.name ?? t('leads.detail.callAction.unnamedContact')} · ${option.phone}`}
              onPress={() => onCallHuman(option.phone)}
            />
          ))
        ) : (
          <>
            <OptionRow
              testID="call_method_human"
              icon={<PhoneCall size={18} color={colors.primary.main} />}
              title={t('leads.detail.callAction.humanOption')}
              hint={t('leads.detail.callAction.humanOptionHint')}
              accessibilityLabel={t('leads.detail.callAction.humanOption')}
              onPress={handleHumanPress}
            />
            <OptionRow
              testID="call_method_agent"
              icon={<Bot size={18} color={colors.primary.main} />}
              title={t('leads.detail.callAction.agentOption')}
              hint={t('leads.detail.callAction.agentOptionHint')}
              accessibilityLabel={t('leads.detail.callAction.agentOption')}
              onPress={onCallAgent}
            />
          </>
        )}
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  optionRow: {
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: tokens.spacing.md,
  },
  optionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.75,
  },
});
